import React, { useEffect, useMemo, useState } from "react";
import {
  Armchair,
  CalendarIcon,
  Check,
  Clock,
  Minus,
  Plus,
  Send,
  Cake,
  PartyPopper,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";

import { TopBar } from "../components/TopBar";
import { ServiceStep } from "../components/ServiceStep";
import { AuthChoiceStep } from "../components/AuthChoiceStep";
import { EventForm } from "../components/EventForm";
import { useApp } from "../context/AppContext";
import {
  fetchPublicConfig,
} from "../lib/api";
import {
  OCCASIONS,
  ALLERGIES,
  dinnerSlotLabel,
} from "../lib/i18n";
import { formatISODate } from "../lib/utils";
import { bookingErrorMessage } from "../lib/bookingErrors";
import { useBookingRequest } from "../hooks/useBookingRequest";
import { useBookingAvailability } from "../hooks/useBookingAvailability";

function normalizeHandle(v) {
  const clean = (v || "").trim().replace(/^@+/, "");
  return clean ? `@${clean}` : "";
}

// ---------------------------------------------------------------------------

function FormField({ id, label, required, error, children, help }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-0.5 text-primary">*</span>}
      </Label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function GuestForm({ onSubmit, onBack, seedInstagram = "", initialUser }) {
  const { t } = useApp();
  const [form, setForm] = useState(initialUser || {
    nome: "",
    cognome: "",
    instagram: seedInstagram.replace(/^@/, ""),
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.nome.trim()) next.nome = t("err_required");
    if (!form.cognome.trim()) next.cognome = t("err_required");
    const handle = normalizeHandle(form.instagram);
    if (!form.email.trim()) {
      next.email = t("err_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = t("err_invalid_email");
    }
    const phoneClean = form.phone.replace(/[^\d+]/g, "");
    if (!phoneClean) {
      next.phone = t("err_required");
    } else if (!/^\+?\d{6,15}$/.test(phoneClean)) {
      next.phone = t("err_invalid_phone");
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    onSubmit({
      nome: form.nome.trim(),
      cognome: form.cognome.trim(),
      instagram: handle,
      email: form.email.trim(),
      phone: phoneClean,
    });
  };

  return (
    <Card
      className="mx-auto max-w-xl animate-fade-slide-up border-border bg-card"
      data-testid="guest-form-card"
    >
      <CardContent className="p-6 sm:p-8">
        <h2 className="font-display text-xl font-semibold">
          {t("guest_title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("guest_subtitle")}
        </p>
        <form className="mt-6 grid gap-4" onSubmit={submit} noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="nome" label={t("field_nome")} required error={errors.nome}>
              <Input
                id="nome"
                maxLength={80}
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="guest-nome-input"
                autoComplete="given-name"
              />
            </FormField>
            <FormField id="cognome" label={t("field_cognome")} required error={errors.cognome}>
              <Input
                id="cognome"
                maxLength={80}
                value={form.cognome}
                onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                data-testid="guest-cognome-input"
                autoComplete="family-name"
              />
            </FormField>
          </div>
          <FormField id="email" label={t("field_email")} required error={errors.email}>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="mario@example.com"
              data-testid="guest-email-input"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
            />
          </FormField>
          <FormField
            id="phone"
            label={t("field_phone")}
            required
            error={errors.phone}
            help={t("phone_hint")}
          >
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+39 333 1234567"
              data-testid="guest-phone-input"
              autoComplete="tel"
              inputMode="tel"
            />
          </FormField>
          <FormField id="instagram" label={t("field_instagram")} error={errors.instagram}>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                @
              </span>
              <Input
                id="instagram"
                maxLength={59}
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                className="pl-7"
                placeholder="mario_rossi"
                data-testid="guest-instagram-input"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          </FormField>

          <div className="mt-2 flex items-center justify-between gap-3">
            <Button type="button" variant="ghost" onClick={onBack} data-testid="guest-back-button">
              ←
            </Button>
            <Button
              type="submit"
              className="h-11 min-w-[10rem] rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="guest-continue-button"
            >
              {t("guest_continue")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function InstagramModal({ open, onOpenChange, onSubmit }) {
  const { t } = useApp();
  const [value, setValue] = useState("");
  const [err, setErr] = useState("");

  const confirm = () => {
    const handle = normalizeHandle(value);
    if (!handle || handle.length < 2) {
      setErr(t("ig_error_required"));
      return;
    }
    onSubmit(handle);
    setValue("");
    setErr("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="instagram-modal">
        <DialogHeader>
          <div className="mx-auto mb-2">
            <img src="https://cdn.simpleicons.org/instagram" alt="Instagram" className="h-8 w-8" />
          </div>
          <DialogTitle className="text-center font-display text-xl">
            {t("ig_modal_title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("ig_modal_subtitle")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label htmlFor="ig-username">{t("ig_username_label")}</Label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              @
            </span>
            <Input
              id="ig-username"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t("ig_username_placeholder")}
              className="pl-7"
              data-testid="instagram-username-input"
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          {err && <p className="text-xs text-destructive" role="alert">{err}</p>}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="instagram-cancel-button">
            {t("ig_cancel")}
          </Button>
          <Button onClick={confirm} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="instagram-confirm-button">
            {t("ig_continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

function SeatRow({ count }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label={`${count} seats`}>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary animate-seat-pop"
          style={{ animationDelay: `${i * 35}ms` }}
        >
          <Armchair className="h-3.5 w-3.5" />
        </span>
      ))}
    </div>
  );
}

function ChipGrid({ values, options, onToggle, multi = false, testIdPrefix }) {
  const isActive = (v) => (multi ? values.includes(v) : values === v);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = isActive(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            aria-pressed={active}
            data-testid={`${testIdPrefix}-${opt.value}`}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all",
              active
                ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
                : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProgressIndicator({ steps }) {
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-full border border-border bg-background/40 p-1.5"
      data-testid="form-progress"
      role="progressbar"
      aria-valuenow={steps.filter((s) => s.done).length}
      aria-valuemax={steps.length}
    >
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <span
            data-testid={`progress-step-${s.key}`}
            data-done={s.done ? "true" : "false"}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-all ${
              s.done
                ? "bg-primary/20 text-primary"
                : "bg-muted/40 text-muted-foreground"
            }`}
          >
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                s.done
                  ? "bg-primary text-primary-foreground"
                  : "border border-muted-foreground/40"
              }`}
            >
              {s.done ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : i + 1}
            </span>
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <span
              className={`h-px w-3 ${s.done ? "bg-primary/50" : "bg-muted-foreground/20"}`}
              aria-hidden
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ReservationForm({ user, service, onSubmitted, onBack, onChangeService }) {
  const { t, lang } = useApp();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [configVersion, setConfigVersion] = useState(0);
  const sendBooking = useBookingRequest();
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);
  const [note, setNote] = useState("");
  const [occasion, setOccasion] = useState("");
  const [occasionNote, setOccasionNote] = useState("");
  const [allergies, setAllergies] = useState([]);
  // { gluten: 2 } — how many of the party each intolerance affects.
  const [allergyCounts, setAllergyCounts] = useState({});
  const [allergiesNote, setAllergiesNote] = useState("");
  const [birthday, setBirthday] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState({}); // { date: true, time: true, terms: true }
  const [submitting, setSubmitting] = useState(false);
  const dateISO = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : "";
  const { availability, ready, loading: availabilityLoading, error: availabilityError, retry: retryAvailability } = useBookingAvailability(dateISO, service, guests);

  const dateRef = React.useRef(null);
  const timeRef = React.useRef(null);
  const termsRef = React.useRef(null);

  // Fetch config (time slots per service, capacity)
  useEffect(() => {
    let active = true;
    setConfigError(false);
    fetchPublicConfig().then((data) => { if (active) setConfig(data); }).catch(() => { if (active) setConfigError(true); });
    return () => { active = false; };
  }, [configVersion]);

  useEffect(() => {
    if (ready && time && (!availability.slots[time] || availability.slots[time].full)) {
      const code = availability.slots[time]?.reason || "invalid_time";
      setTime("");
      setError(bookingErrorMessage({ response: { status: 409, data: { detail: { code } } } }, lang));
    }
  }, [availability, ready, time, lang]);

  const slots = ready ? Object.keys(availability.slots).sort() : [];
  const deposit = Number.isFinite(config?.depositPerPerson) ? guests * config.depositPerPerson : null;
  const toggleAllergy = (value) => {
    const on = allergies.includes(value);
    setAllergies((curr) =>
      on ? curr.filter((x) => x !== value) : [...curr, value]
    );
    setAllergyCounts(({ [value]: _removed, ...rest }) =>
      on ? rest : { ...rest, [value]: 1 }
    );
  };

  const setAllergyCount = (value, n) =>
    setAllergyCounts((curr) => ({ ...curr, [value]: Math.min(Math.max(1, n), guests) }));

  // Party shrank below a headcount we already have: clamp, or the backend
  // rejects the booking with a 400 the customer cannot act on.
  useEffect(() => {
    setAllergyCounts((curr) => {
      const capped = Object.fromEntries(
        Object.entries(curr).map(([k, v]) => [k, Math.min(v, guests)])
      );
      return Object.entries(capped).some(([k, v]) => curr[k] !== v) ? capped : curr;
    });
  }, [guests]);

  const notesLeft = 300 - note.length;

  const formatDate = (d) =>
    d ? d.toLocaleDateString("it-IT", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    }) : "";

  const toISO = (d) => {
    if (!d) return "";
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");

    // Granular validation: report exactly which field is missing
    const miss = {};
    if (!date) miss.date = true;
    if (!time || (ready && availability.slots[time]?.full)) miss.time = true;
    if (!terms) miss.terms = true;
    setMissing(miss);

    if (miss.date || miss.time) {
      // Build specific message
      const parts = [];
      if (miss.date) parts.push(t("field_date_lc") || t("field_date"));
      if (miss.time) parts.push(t("field_time_lc") || t("field_time"));
      setError(`${t("submit_err_missing_specific")} ${parts.join(" + ")}`);
      // Scroll to first missing field
      const target = miss.date ? dateRef.current : timeRef.current;
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (miss.terms) {
      setError(t("submit_err_terms"));
      if (termsRef.current) termsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!ready || deposit === null) {
      setError(t("availability_unavailable"));
      retryAvailability();
      if (deposit === null) setConfigVersion((v) => v + 1);
      return;
    }
    try {
      setSubmitting(true);
      const booking = await sendBooking({
        nome: user.nome,
        cognome: user.cognome || "",
        instagram: user.instagram || "",
        email: user.email,
        phone: user.phone,
        service,
        date: toISO(date),
        time,
        guests,
        note,
        occasion,
        occasion_note: occasion === "other" ? occasionNote : "",
        allergies,
        allergy_counts: allergyCounts,
        allergies_note: allergiesNote,
        birthday: birthday || null,
      });
      onSubmitted(booking);
    } catch (err) {
      setError(bookingErrorMessage(err, lang));
      const code = err?.response?.data?.detail?.code;
      if (["slot_full", "two_top_full", "past_slot", "invalid_time", "lunch_sunday_only"].includes(code)) {
        setTime("");
        retryAvailability();
        timeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const occasionOptions = OCCASIONS.map((o) => ({ value: o, label: t(`occasion_${o}`) }));
  const allergyOptions = ALLERGIES.map((a) => ({ value: a, label: t(`allergy_${a}`) }));

  return (
    <div className="mx-auto max-w-2xl animate-fade-slide-up" data-testid="reservation-form">
      <Card className="mb-4 border-border bg-card">
        <CardContent className="flex items-center justify-between gap-3 p-4 sm:p-5">
          <div>
            <h2 className="font-display text-lg font-semibold sm:text-xl">
              {t("hello")}, {user.nome}!
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {user.instagram && (
                <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary" data-testid="user-handle-badge">
                  {user.instagram}
                </Badge>
              )}
              <Badge variant="outline" className="border-primary/50 text-primary" data-testid="service-badge">
                {t(`service_${service}`)}
              </Badge>
              <button
                type="button"
                onClick={onChangeService}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
                data-testid="change-service-link"
              >
                {t("change_service")}
              </button>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="back" data-testid="reservation-back-button">←</Button>
        </CardContent>
      </Card>

      <Card className="border-border bg-card shadow-sm">
        <CardContent className="space-y-6 p-5 sm:p-8">
          <div>
            <h3 className="font-display text-lg font-semibold">{t("step_title")}</h3>
            <p className="text-sm text-muted-foreground">{t("step_subtitle")}</p>
          </div>

          {/* Mini progress indicator */}
          <ProgressIndicator
            steps={[
              { key: "date", label: t("progress_date"), done: !!date },
              { key: "time", label: t("progress_time"), done: !!time },
              { key: "guests", label: t("progress_guests"), done: guests >= 1 },
              { key: "terms", label: t("progress_terms"), done: terms },
            ]}
          />

          <form onSubmit={submit} className="space-y-6">
            {/* Date */}
            <div ref={dateRef} className="space-y-2" data-testid="field-date">
              <Label htmlFor="date">
                {t("field_date")} <span className="text-primary">*</span>
              </Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="date"
                    type="button"
                    variant="outline"
                    className={`h-11 w-full justify-start gap-2 bg-background/70 font-normal ${
                      missing.date ? "border-2 border-destructive" : "border-input"
                    } ${!date ? "text-muted-foreground" : "text-foreground"}`}
                    data-testid="booking-date-input"
                  >
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    {date ? formatDate(date) : t("date_placeholder")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0" data-testid="booking-date-popover">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setDateOpen(false);
                      setTime("");
                      setError("");
                      setMissing((m) => ({ ...m, date: false }));
                    }}
                    disabled={(d) => d < today || d > maxDate || (service === "lunch" && d.getDay() !== 0)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {missing.date && (
                <p className="text-xs text-destructive" data-testid="error-date">
                  {t("field_date_required")}
                </p>
              )}
            </div>

            {/* Time slots */}
            <fieldset
              ref={timeRef}
              data-testid="field-time"
              className={`space-y-2 ${missing.time ? "rounded-lg border-2 border-destructive p-3" : ""}`}
            >
              <legend className="text-sm font-medium text-foreground">
                <Clock className="mr-1.5 inline h-4 w-4 text-primary" />
                {service === "dinner" ? t("field_time_dinner") : t("field_time")}{" "}
                <span className="text-primary">*</span>
              </legend>
              {service === "lunch" && <p className="text-xs text-primary" data-testid="lunch-sunday-notice">{t("lunch_sunday_only_notice")}</p>}
              {!date && <p className="text-sm text-muted-foreground" data-testid="availability-select-date">{t("availability_select_date")}</p>}
              {availabilityLoading && <p role="status" className="text-sm text-muted-foreground" data-testid="availability-loading">{t("availability_loading")}</p>}
              {availabilityError && (
                <div role="alert" className="space-y-2 text-sm text-destructive" data-testid="availability-error">
                  <p>{t("availability_unavailable")}</p>
                  <Button type="button" variant="outline" onClick={retryAvailability} data-testid="availability-retry-button">{t("retry")}</Button>
                </div>
              )}
              {ready && slots.every((slot) => availability.slots[slot].full) && <p role="status" className="text-sm text-muted-foreground" data-testid="availability-no-slots">{t("availability_no_slots")}</p>}
              {service === "dinner" ? (
                <div className="grid gap-2">
                  {slots.map((slot) => {
                    const info = availability?.slots?.[slot];
                    const full = info?.full;
                    const active = time === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => { if (!full) { setTime(slot); setError(""); setMissing((m) => ({ ...m, time: false })); } }}
                        disabled={full}
                        data-testid={`time-slot-${slot}`}
                        className={[
                          "flex flex-wrap items-center justify-between gap-2 rounded-2xl border-2 px-5 py-3 text-left transition-colors",
                          full
                            ? "cursor-not-allowed border-border bg-muted/30 text-muted-foreground line-through"
                            : active
                              ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.22)]"
                              : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10",
                        ].join(" ")}
                        aria-pressed={active}
                      >
                        <span className="font-display text-base font-semibold">
                          {dinnerSlotLabel(dateISO, slot)}
                        </span>
                        {full && (
                          <span className="text-xs text-muted-foreground" data-testid={`time-slot-reason-${slot}`}>
                            {t(info.reason === "two_top_full" ? "slot_two_top_full" : info.reason === "past_slot" ? "slot_past" : "slot_full")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-1 scrollbar-thin sm:flex-wrap sm:overflow-visible">
                  {slots.map((slot) => {
                    const active = time === slot;
                    const info = availability?.slots?.[slot];
                    const full = info?.full;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => { if (!full) { setTime(slot); setError(""); setMissing((m) => ({ ...m, time: false })); } }}
                        disabled={full}
                        title={full ? t(info.reason === "past_slot" ? "slot_past" : "slot_full") : undefined}
                        data-testid={`time-slot-${slot}`}
                        className={[
                          "h-11 min-w-[78px] shrink-0 snap-start rounded-full border-2 px-4 text-sm font-medium transition-all",
                          full
                            ? "cursor-not-allowed border-border bg-muted/30 text-muted-foreground line-through"
                            : active
                            ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.22)]"
                            : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
                        ].join(" ")}
                        aria-pressed={active}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              )}
              {missing.time && (
                <p className="text-xs text-destructive" data-testid="error-time">
                  {t("field_time_required")}
                </p>
              )}
            </fieldset>

            {/* Guests */}
            <div className="space-y-3">
              <Label>
                {t("field_guests")} <span className="text-primary">*</span>
              </Label>
              <div className="flex items-center gap-5">
                <Button type="button" variant="outline" size="icon" className="h-11 w-11 rounded-full"
                  onClick={() => setGuests((g) => Math.max(1, g - 1))} aria-label="decrease guests"
                  data-testid="guests-minus" disabled={guests <= 1}>
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="min-w-[3rem] text-center font-display text-2xl font-semibold tabular-nums text-primary"
                  data-testid="guests-count">{guests}</div>
                <Button type="button" variant="outline" size="icon" className="h-11 w-11 rounded-full"
                  onClick={() => setGuests((g) => Math.min(40, g + 1))} aria-label="increase guests"
                  data-testid="guests-plus" disabled={guests >= 40}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <SeatRow count={guests} />
            </div>

            {/* Occasion */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <PartyPopper className="h-4 w-4 text-primary" />
                {t("field_occasion")}
              </Label>
              <ChipGrid
                values={occasion}
                options={occasionOptions}
                onToggle={(v) => setOccasion(occasion === v ? "" : v)}
                testIdPrefix="occasion"
              />
              {occasion === "other" && (
                <Input
                  placeholder={t("field_occasion_other")}
                  value={occasionNote}
                  maxLength={120}
                  onChange={(e) => setOccasionNote(e.target.value)}
                  data-testid="occasion-other-input"
                />
              )}
            </div>

            {/* Allergies */}
            <div className="space-y-2">
              <Label>{t("field_allergies")}</Label>
              <ChipGrid
                values={allergies}
                options={allergyOptions}
                multi
                onToggle={toggleAllergy}
                testIdPrefix="allergy"
              />
              {allergies.length > 0 && (
                <fieldset
                  className="space-y-2 rounded-lg border border-border bg-background/40 p-3"
                  data-testid="allergy-counts"
                >
                  <legend className="px-1 text-xs text-muted-foreground">
                    {t("allergy_counts_label")}
                  </legend>
                  {allergies.map((a) => (
                    <div key={a} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-sm">
                        {t(`allergy_${a}`)}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setAllergyCount(a, (allergyCounts[a] || 1) - 1)}
                          disabled={(allergyCounts[a] || 1) <= 1}
                          aria-label={`${t("allergy_count_decrease")} — ${t(`allergy_${a}`)}`}
                          data-testid={`allergy-count-minus-${a}`}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        {/* <output> is an implicit live region: the new value
                            is announced when the stepper is pressed. */}
                        <output
                          aria-label={t(`allergy_${a}`)}
                          className="min-w-[2rem] text-center text-sm font-semibold tabular-nums text-primary"
                          data-testid={`allergy-count-${a}`}
                        >
                          {allergyCounts[a] || 1}
                        </output>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          onClick={() => setAllergyCount(a, (allergyCounts[a] || 1) + 1)}
                          disabled={(allergyCounts[a] || 1) >= guests}
                          aria-label={`${t("allergy_count_increase")} — ${t(`allergy_${a}`)}`}
                          data-testid={`allergy-count-plus-${a}`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <p className="px-1 text-xs text-muted-foreground">
                    {t("allergy_counts_hint")}
                  </p>
                </fieldset>
              )}
              <Input
                placeholder={t("field_allergies_note")}
                value={allergiesNote}
                maxLength={200}
                onChange={(e) => setAllergiesNote(e.target.value)}
                data-testid="allergies-note-input"
              />
            </div>

            {/* Birthday */}
            <FormField id="birthday" label={t("field_birthday")} help={t("birthday_hint")}>
              <div className="relative">
                <Cake className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
                <Input
                  id="birthday"
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  className="pl-9"
                  data-testid="birthday-input"
                />
              </div>
            </FormField>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="note">{t("field_notes")}</Label>
              <Textarea
                id="note"
                value={note}
                maxLength={300}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("notes_placeholder")}
                rows={3}
                data-testid="booking-note-input"
                autoComplete="off"
              />
              <p className="text-right text-xs text-muted-foreground">
                {notesLeft} {t("chars_left")}
              </p>
            </div>

            {/* Deposit */}
            <div className="rounded-xl border border-primary/40 bg-primary/10 p-4" data-testid="deposit-box">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-foreground">{t("deposit_label")}</span>
                <span className="font-display text-xl font-semibold text-primary" data-testid="deposit-amount">
                  {deposit === null ? "—" : `€${deposit}`}
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{t("deposit_hint")}</p>
              {configError && <div role="alert" data-testid="booking-config-error" className="mt-2 space-y-2 text-sm text-destructive"><p>{t("availability_unavailable")}</p><Button type="button" variant="outline" onClick={() => setConfigVersion((v) => v + 1)} data-testid="booking-config-retry">{t("retry")}</Button></div>}
            </div>

            {/* Dinner-only disclaimer */}
            {service === "dinner" && (
              <p
                className="text-xs italic text-muted-foreground"
                data-testid="dinner-only-notice"
              >
                {t("dinner_only_notice")}
              </p>
            )}

            {/* Terms */}
            <label
              ref={termsRef}
              data-testid="field-terms"
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                missing.terms
                  ? "border-2 border-destructive bg-destructive/5"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <Checkbox
                checked={terms}
                onCheckedChange={(v) => { setTerms(!!v); setMissing((m) => ({ ...m, terms: false })); }}
                data-testid="terms-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">{t("terms_checkbox")}</span>
            </label>

            {error && (
              <p className="text-sm text-destructive" role="alert" data-testid="booking-error">{error}</p>
            )}

            <Button
              type="submit"
              className="min-h-12 h-auto w-full gap-2 whitespace-normal rounded-full bg-primary px-4 py-3 text-primary-foreground hover:bg-primary/90"
              disabled={submitting || availabilityLoading || (date && !ready) || deposit === null}
              data-testid="booking-submit-button"
            >
              <Send className="h-4 w-4 shrink-0" />
              {t(submitting ? "submitting_request" : "submit_request")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SuccessScreen({ booking, onAnother }) {
  const { t, lang, restaurantWhatsapp } = useApp();

  const isEvent = booking.service === "event";
  const serviceLabel = t(`service_${booking.service}`);
  const waMessage = isEvent
    ? lang === "en"
      ? `Hi Vagalume, I just sent an event request:%0A%0A• Type: ${serviceLabel}%0A• Name: ${booking.nome} ${booking.cognome}%0A• Preferred date: ${formatISODate(booking.date)}%0A• Guests: ${booking.guests}%0A%0ACan you get back to me? Thank you!`
      : `Ciao Vagalume, ho appena inviato una richiesta evento:%0A%0A• Tipo: ${serviceLabel}%0A• Nome: ${booking.nome} ${booking.cognome}%0A• Data preferita: ${formatISODate(booking.date)}%0A• Ospiti: ${booking.guests}%0A%0APotete ricontattarmi? Grazie!`
    : lang === "en"
      ? `Hi Vagalume, I just made a reservation:%0A%0A• Service: ${serviceLabel}%0A• Name: ${booking.nome} ${booking.cognome}%0A• Date: ${formatISODate(booking.date)}%0A• Time: ${booking.time}%0A• Guests: ${booking.guests}%0A• Deposit: €${booking.deposit}%0A%0ACan you confirm? Thank you!`
      : `Ciao Vagalume, ho appena prenotato:%0A%0A• Servizio: ${serviceLabel}%0A• Nome: ${booking.nome} ${booking.cognome}%0A• Data: ${formatISODate(booking.date)}%0A• Orario: ${booking.time}%0A• Coperti: ${booking.guests}%0A• Anticipo: €${booking.deposit}%0A%0APotete confermare? Grazie!`;
  const waUrl = restaurantWhatsapp
    ? `https://wa.me/${restaurantWhatsapp}?text=${waMessage}`
    : null;

  // Auto-redirect to WhatsApp after a brief delay
  React.useEffect(() => {
    if (!waUrl) return;
    const timer = setTimeout(() => { window.location.href = waUrl; }, 1800);
    return () => clearTimeout(timer);
  }, [waUrl]);

  return (
    <div className="mx-auto max-w-xl animate-fade-slide-up text-center" data-testid="success-screen">
      <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-primary/15 animate-pulse-glow">
        <Clock className="h-10 w-10 text-primary" />
      </div>
      <h2 className="font-display text-xl font-semibold sm:text-2xl">
        {isEvent ? t("event_success_title") : t("success_title")}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground sm:text-base">
        {isEvent ? t("event_success_subtitle") : t("success_subtitle")}
      </p>

      <Card className="mt-8 border-border bg-card text-left">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-lg font-semibold">{booking.nome} {booking.cognome}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                <Badge variant="outline" className="border-primary/50 text-primary">{serviceLabel}</Badge>
                {booking.instagram && (
                  <Badge variant="outline" className="border-primary/40 text-primary">{booking.instagram}</Badge>
                )}
              </div>
            </div>
            <Badge role="status" aria-label={t("status_pending")}
              className="animate-pulse-glow border-primary/50 bg-primary/20 text-primary hover:bg-primary/25"
              data-testid="success-status-badge">
              {t("status_pending_short")}
            </Badge>
          </div>

          <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-muted-foreground">{t("summary_date")}</dt><dd className="font-medium">{formatISODate(booking.date)}</dd></div>
            <div><dt className="text-muted-foreground">{t("summary_guests")}</dt><dd className="font-medium">{booking.guests}</dd></div>
            {!isEvent && (
              <>
                <div><dt className="text-muted-foreground">{t("summary_time")}</dt>
                  <dd className="font-medium">
                    {booking.service === "dinner" ? dinnerSlotLabel(booking.date, booking.time) : booking.time}
                  </dd>
                </div>
                <div><dt className="text-muted-foreground">{t("summary_deposit")}</dt><dd className="font-medium text-primary">€{booking.deposit}</dd></div>
              </>
            )}
          </dl>

          <p className="mt-4 text-xs text-muted-foreground">{t("status_pending_note")}</p>
        </CardContent>
      </Card>

      {waUrl && (
        <div className="mt-6 flex flex-col items-center gap-2" data-testid="whatsapp-auto-redirect">
          <p className="inline-flex items-center gap-2 text-sm text-[#25D366]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#25D366]"></span>
            </span>
            {t("whatsapp_redirecting")}
          </p>
          <a href={waUrl} className="text-xs text-muted-foreground underline-offset-4 hover:text-primary hover:underline" data-testid="whatsapp-manual-fallback">
            {t("whatsapp_open_manual")}
          </a>
        </div>
      )}

      <div className="mt-4">
        <Button variant="ghost" className="h-11 rounded-full" onClick={onAnother} data-testid="make-another-button">
          {t("make_another")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function BookingPage() {
  // step: 'service' | 'auth' | 'guest' | 'form' | 'success'
  const [step, setStep] = useState("service");
  const [service, setService] = useState(null);
  const [igOpen, setIgOpen] = useState(false);
  const [igHandle, setIgHandle] = useState("");
  const [user, setUser] = useState(null);
  const [booking, setBooking] = useState(null);
  const { t } = useApp();

  const handleServiceSelect = (id) => {
    setService(id);
    setStep("auth");
  };

  const handleEventCTA = () => {
    setService("event");
    setStep("auth");
  };

  const handleInstagramSubmit = (handle) => {
    setIgHandle(handle);
    setIgOpen(false);
    setStep("guest");
    toast.success(`${t("hello")}, ${handle}`);
  };

  const handleGuestSubmit = (data) => {
    setUser(data);
    setStep("form");
  };

  const handleBooked = (b) => {
    setBooking(b);
    setStep("success");
  };

  const resetFlow = () => {
    setBooking(null);
    setUser(null);
    setIgHandle("");
    setService(null);
    setStep("service");
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-14">
        {step === "service" && (
          <ServiceStep onSelect={handleServiceSelect} onEvent={handleEventCTA} />
        )}
        {step === "auth" && service && (
          <AuthChoiceStep
            service={service}
            onInstagram={() => setIgOpen(true)}
            onGuest={() => setStep("guest")}
            onBack={() => { setService(null); setStep("service"); }}
          />
        )}
        {step === "guest" && (
          <GuestForm
            onSubmit={handleGuestSubmit}
            onBack={() => setStep("auth")}
            seedInstagram={igHandle}
            initialUser={user}
          />
        )}
        {step === "form" && user && service && service !== "event" && (
          <ReservationForm
            user={user}
            service={service}
            onSubmitted={handleBooked}
            onBack={() => setStep("guest")}
            onChangeService={() => setStep("service")}
          />
        )}
        {step === "form" && user && service === "event" && (
          <EventForm
            user={user}
            onSubmitted={handleBooked}
            onBack={() => setStep("guest")}
          />
        )}
        {step === "success" && booking && (
          <SuccessScreen booking={booking} onAnother={resetFlow} />
        )}
      </main>

      <footer className="mx-auto max-w-5xl px-4 pb-10 pt-4 text-center sm:px-6">
        <div className="font-display italic text-base text-foreground/80 underline decoration-foreground/30 underline-offset-4">
          {t("address_line_1")},<br />{t("address_line_2")}
        </div>
        <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("copyright")}
        </p>
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="credits">
          {t("credits_developed_by")}{" "}
          <span className="font-medium text-foreground/75">GS Cyber Solution</span>
          <span aria-hidden="true"> · </span>
          <a
            href="mailto:info@gscybersolution.it"
            className="underline decoration-muted-foreground/40 underline-offset-2 transition-colors hover:text-primary focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="credits-email"
          >
            info@gscybersolution.it
          </a>
        </p>
      </footer>

      <InstagramModal open={igOpen} onOpenChange={setIgOpen} onSubmit={handleInstagramSubmit} />
    </div>
  );
}
